// src/components/ui/DocsDashboardSkeleton.jsx
import React from "react";
import { Box, Skeleton, Paper, Stack, Divider } from "@mui/material";

/**
 * DocsDashboardSkeleton
 * Shape-matching skeleton for the Documents Dashboard:
 * - Header/search strip
 * - Grid of client cards
 * - Three list sections: Recent Uploads, One-Time Uploads, Form Templates
 */
export default function DocsDashboardSkeleton({
  clientCardCount = 4,
  listRows = 5,
}) {
  const renderClientCards = () => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr 1fr" },
        gap: 2,
        mb: 4,
      }}
    >
      {Array.from({ length: clientCardCount }).map((_, i) => (
        <Paper
          key={`client-skel-${i}`}
          variant="outlined"
          sx={{ p: 2, minHeight: 180, display: "flex", flexDirection: "column" }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="70%" height={20} />
              <Skeleton variant="text" width="50%" height={16} />
            </Box>
          </Stack>

          <Divider sx={{ my: 1 }} />

          {/* Assigned docs mini-list inside card */}
          <Stack spacing={1} sx={{ flex: 1 }}>
            <Skeleton variant="text" width="90%" height={16} />
            <Skeleton variant="text" width="65%" height={16} />
            <Skeleton variant="text" width="80%" height={16} />
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Skeleton variant="rectangular" height={32} sx={{ flex: 1 }} />
            <Skeleton variant="rectangular" height={32} sx={{ width: 96 }} />
          </Stack>
        </Paper>
      ))}
    </Box>
  );

  const renderRecentUploads = () => (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Skeleton variant="text" width={260} height={24} />
      <Divider sx={{ my: 1.5 }} />
      <Stack spacing={1.5}>
        {Array.from({ length: listRows }).map((_, idx) => (
          <Stack
            key={`recent-${idx}`}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="40%" height={18} />
              <Skeleton variant="text" width="25%" height={14} />
            </Box>
            <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
              <Skeleton variant="rectangular" width={88} height={30} />
              <Skeleton variant="rectangular" width={140} height={30} />
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );

  const renderOneTimeUploads = () => (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Skeleton variant="text" width={220} height={24} />
      <Divider sx={{ my: 1.5 }} />
      <Stack spacing={1.5}>
        {Array.from({ length: listRows }).map((_, idx) => (
          <Stack
            key={`one-time-${idx}`}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="38%" height={18} />
              <Skeleton variant="text" width="24%" height={14} />
            </Box>
            <Skeleton variant="rectangular" width={88} height={30} />
          </Stack>
        ))}
      </Stack>
    </Paper>
  );

  const renderFormTemplates = () => (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Skeleton variant="text" width={240} height={24} />
      <Divider sx={{ my: 1.5 }} />
      <Stack spacing={1.5}>
        {Array.from({ length: listRows }).map((_, idx) => (
          <Stack
            key={`templates-${idx}`}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 0.5 }}
          >
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="45%" height={18} />
              <Skeleton variant="text" width="28%" height={14} />
            </Box>
            <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
              <Skeleton variant="rectangular" width={96} height={30} />
              <Skeleton variant="rectangular" width={168} height={30} />
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      {/* Header/Search bar stub */}
      <Box sx={{ mb: 2 }}>
        <Skeleton variant="text" width={220} height={32} />
        <Skeleton variant="rectangular" height={40} />
      </Box>

      {renderClientCards()}
      {renderRecentUploads()}
      {renderOneTimeUploads()}
      {renderFormTemplates()}
    </Box>
  );
}